import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  InputLabel,
  makeStyles,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  Switch,
  FormControlLabel
} from '@material-ui/core';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { createGeocodeSource, reloadGeocode } from 'src/features/geocodeSlice';
import axios from 'src/app/axiosInterceptor';

/**
 * Ba loại nguồn mà ingress đọc được — bản sao của `SupportedTypes()` trong
 * vgps_ingress_api (project/services/geocode/source.go). Dùng khi API chưa trả về danh sách.
 *
 * Phải so bằng ĐỘ DÀI chứ không bằng chân trị: `[]` trong JS là truthy, nên `supportedTypes || […]`
 * giữ nguyên mảng rỗng và ô chọn loại không có gì để mở — đúng cảnh gặp khi API /geocode 404.
 */
const FALLBACK_TYPES = ['nominatim', 'photon', 'camnet'];

const initStateFromValue = {
  name: '',
  type: '',
  url: '',
  priority: 100,
  enabled: true,
  rate_limit_per_sec: '',
  timeout_ms: 2000,
  max_retries: 2,
  reject_weak: false,
  api_key: '',
  api_key_param: ''
};

function CreateGeocodeSource({ open, onClose, supportedTypes }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const handleClose = () => {
    onClose();
    setTestResult(null);
  };

  const handleTest = (values) => {
    setTesting(true);
    const testBody = {
      type: values.type,
      url: values.url,
      api_key: values.api_key || '',
      api_key_param: values.api_key_param || '',
      lat: 21.028511,
      lng: 105.804817
    };

    axios.post('/geocode/sources/test', testBody)
      .then(res => {
        const data = res.data;
        if (data.success && data.payload) {
          setTestResult({
            ok: true,
            address: data.payload.address || data.payload.display_name || '',
            duration_ms: data.payload.duration_ms
          });
        } else {
          setTestResult({
            ok: false,
            address: data.message || 'Test thất bại'
          });
        }
        setTesting(false);
      })
      .catch(err => {
        setTestResult({
          ok: false,
          address: err?.data?.message || err?.message || 'Lỗi test'
        });
        setTesting(false);
      });
  };

  const handleSubmit = data => {
    const body = {
      name: data.name,
      type: data.type,
      url: data.url,
      priority: Number(data.priority),
      enabled: data.enabled,
      rate_limit_per_sec: data.rate_limit_per_sec === '' ? 0 : Number(data.rate_limit_per_sec),
      timeout_ms: Number(data.timeout_ms),
      max_retries: Number(data.max_retries),
      reject_weak: data.reject_weak,
      api_key_param: data.api_key_param || ''
    };
    if (data.api_key) {
      body.api_key = data.api_key;
    }

    dispatch(createGeocodeSource(body))
      .then(result => {
        if (result.type === 'geocodeSlice/createGeocodeSource/fulfilled') {
          dispatch(reloadGeocode({}));
          onClose();
        }
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { borderRadius: 8 }
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" className={classes.titleText}>
          Thêm mới nguồn bản đồ
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Formik
          initialValues={initStateFromValue}
          validationSchema={Yup.object().shape({
            name: Yup.string()
              .max(100)
              .required('Tên nguồn không được để trống'),
            type: Yup.string()
              .required('Loại nguồn không được để trống'),
            url: Yup.string()
              .matches(/^https?:\/\/.+/, 'URL phải bắt đầu bằng http:// hoặc https://')
              .required('URL không được để trống'),
            priority: Yup.number()
              .typeError('Phải là số')
              .required('Độ ưu tiên không được để trống'),
            timeout_ms: Yup.number()
              .typeError('Phải là số')
              .min(100, 'Tối thiểu 100ms'),
            max_retries: Yup.number()
              .typeError('Phải là số')
              .min(0, 'Tối thiểu 0'),
            rate_limit_per_sec: Yup.number()
              .typeError('Phải là số')
              .min(0, 'Tối thiểu 0')
          })}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InputLabel className={classes.label}>
                    Tên nguồn <span style={{ color: 'red' }}>*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.name && errors.name)}
                    fullWidth
                    helperText={touched.name && errors.name}
                    size="small"
                    name="name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    variant="outlined"
                    placeholder="Nhập tên nguồn"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InputLabel className={classes.label}>
                    Loại <span style={{ color: 'red' }}>*</span>
                  </InputLabel>
                  <Select
                    error={Boolean(touched.type && errors.type)}
                    fullWidth
                    size="small"
                    name="type"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.type}
                    variant="outlined"
                    className={`${classes.textField} ${classes.select}`}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Chọn loại nguồn</MenuItem>
                    {(supportedTypes?.length ? supportedTypes : FALLBACK_TYPES).map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                  {touched.type && errors.type && (
                    <Typography variant="caption" style={{ color: '#f44336' }}>
                      {errors.type}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <InputLabel className={classes.label}>
                    URL <span style={{ color: 'red' }}>*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.url && errors.url)}
                    fullWidth
                    helperText={(touched.url && errors.url) || 'Địa chỉ gốc, không kèm ?lat=&lon= — dịch vụ tự nối'}
                    size="small"
                    name="url"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.url}
                    variant="outlined"
                    placeholder="http://example.com/v1/reverse"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <InputLabel className={classes.label}>
                    Độ ưu tiên <span style={{ color: 'red' }}>*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.priority && errors.priority)}
                    fullWidth
                    helperText={(touched.priority && errors.priority) || 'Nhỏ hơn = được gọi trước'}
                    type="number"
                    size="small"
                    name="priority"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.priority}
                    variant="outlined"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <InputLabel className={classes.label}>
                    Timeout (ms)
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.timeout_ms && errors.timeout_ms)}
                    fullWidth
                    helperText={touched.timeout_ms && errors.timeout_ms}
                    type="number"
                    size="small"
                    name="timeout_ms"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.timeout_ms}
                    variant="outlined"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <InputLabel className={classes.label}>
                    Số lần thử lại
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.max_retries && errors.max_retries)}
                    fullWidth
                    helperText={touched.max_retries && errors.max_retries}
                    type="number"
                    size="small"
                    name="max_retries"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.max_retries}
                    variant="outlined"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <InputLabel className={classes.label}>
                    Giới hạn (req/s)
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.rate_limit_per_sec && errors.rate_limit_per_sec)}
                    fullWidth
                    helperText={(touched.rate_limit_per_sec && errors.rate_limit_per_sec) || '0 = không giới hạn'}
                    type="number"
                    size="small"
                    name="rate_limit_per_sec"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.rate_limit_per_sec}
                    variant="outlined"
                    placeholder="0"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <InputLabel className={classes.label}>
                    API Key Param
                  </InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="api_key_param"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.api_key_param}
                    variant="outlined"
                    helperText="Rỗng = khoá đi ở header X-Api-Key"
                    // Trình duyệt thấy ô trống cạnh một ô mật khẩu là nhét số điện thoại / email đã
                    // lưu vào. Ở đây thứ tự điền đó tạo ra một nguồn hỏng mà nhìn vẫn như đã điền
                    // đúng: khoá bị đẩy lên query string với cái tên vô nghĩa và server trả 401.
                    autoComplete="new-password"
                    inputProps={{ autoComplete: 'new-password' }}
                    placeholder="key"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <InputLabel className={classes.label}>
                    API Key
                  </InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="api_key"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.api_key}
                    variant="outlined"
                    type="password"
                    autoComplete="new-password"
                    inputProps={{ autoComplete: 'new-password' }}
                    placeholder="Nhập API Key (nếu có)"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.enabled}
                        onChange={(e) => setFieldValue('enabled', e.target.checked)}
                        name="enabled"
                        color="primary"
                      />
                    }
                    label="Kích hoạt"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.reject_weak}
                        onChange={(e) => setFieldValue('reject_weak', e.target.checked)}
                        name="reject_weak"
                        color="primary"
                      />
                    }
                    label="Từ chối kết quả yếu"
                  />
                </Grid>

                {testResult && (
                  <Grid item xs={12}>
                    <Box
                      className={classes.testResult}
                      style={{ backgroundColor: testResult.ok ? '#e8f5e9' : '#ffebee' }}
                    >
                      <Typography variant="body2">
                        {testResult.ok
                          ? `OK: ${testResult.address} (${testResult.duration_ms}ms)`
                          : `Lỗi: ${testResult.address}`}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              <DialogActions className={classes.dialogActions}>
                {/* Trước đây "Hủy" cũng là nút đặc màu chính, nhìn ngang hàng với nút gửi form —
                    hai việc trái ngược nhau mà trông như nhau. Giờ chỉ nút gửi được đặc màu. */}
                <Button
                  className={classes.testButton}
                  onClick={() => handleTest(values)}
                  disabled={testing || !values.type || !values.url}
                  variant="outlined"
                  color="secondary"
                >
                  {testing ? 'Đang test...' : 'Test'}
                </Button>
                <Button onClick={handleClose} variant="outlined">
                  Hủy
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  Thêm mới
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
  },
  // MUI v4 chưa có prop `size` cho Select, nên `size="small"` bị bỏ qua và ô Loại cao hơn hẳn ô
  // bên cạnh. Ép đúng khoảng đệm mà TextField size="small" dùng để hai ô bằng nhau.
  select: {
    '& .MuiSelect-outlined': {
      paddingTop: 10.5,
      paddingBottom: 10.5
    }
  },
  titleText: {
    fontSize: '18px',
    fontWeight: 600
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    // Cao cố định một dòng. Nhãn dài hơn sẽ đẩy ô nhập của nó tụt xuống so với ô bên cạnh và
    // làm lệch cả hàng — phần giải thích vì thế chuyển hết xuống helperText.
    lineHeight: '20px',
    minHeight: 20,
    marginBottom: theme.spacing(0.75)
  },
  textField: {
    // Khoảng cách giữa các hàng do Grid spacing lo; thêm margin ở đây thì hàng nào có
    // helperText sẽ dày hơn hàng không có.
    marginBottom: 0
  },
  dialogActions: {
    // Hàng nút nằm TRONG DialogContent (nó ở trong <form>), nên không dùng được `dividers`
    // của DialogContent — kẻ viền trên tại đây mới ra đúng chỗ ngăn giữa biểu mẫu và nút.
    padding: theme.spacing(2, 0, 0),
    marginTop: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    justifyContent: 'flex-end'
  },
  // Test là hành động phụ, đứng cùng hàng nhưng dạt hẳn về trái để không lẫn với nút chính.
  testButton: {
    marginRight: 'auto'
  },
  testResult: {
    borderRadius: 4,
    padding: theme.spacing(1, 1.5)
  }
}));

export default React.memo(CreateGeocodeSource);